import { useEffect, useState, useMemo } from "react";
import client from "@/lib/api";
import {
  Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, Eye, FileText, Package, Award, Inbox, Mail, Users, RefreshCw, UserCheck,
} from "lucide-react";

const RANGE_OPTIONS = [
  { key: 7,  label: "7D" },
  { key: 14, label: "14D" },
  { key: 30, label: "30D" },
  { key: 90, label: "90D" },
];

const REFRESH_MS = 30_000;

export default function AdminOverview() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await client.get("/admin/analytics/overview", { params: { days } });
      setData(r.data);
      setLastUpdated(new Date());
    } catch (e) {
      // keep stale data on transient failures
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Reload on range change.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  // Auto-refresh while tab is visible.
  useEffect(() => {
    let timer = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, REFRESH_MS);
    return () => clearInterval(timer);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [days]);

  if (loading || !data) {
    return (
      <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow py-12 text-center" data-testid="admin-overview-loading">
        // loading analytics...
      </div>
    );
  }

  return (
    <div data-testid="admin-overview-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase">
            Overview
          </h2>
          <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1">
            Live page views and site-wide counts. Auto-refreshes every 30s.{" "}
            {lastUpdated && (
              <span className="font-mono text-[10px] text-[var(--t-text-muted)]">
                · last update {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          className="btn-ghost !text-xs"
          data-testid="admin-overview-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Top KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" data-testid="admin-overview-kpis">
        <Kpi
          icon={Eye}
          label="Today's Views"
          value={data.today_total}
          delta={data.today_total - data.yesterday_total}
          accent="var(--t-cyan)"
        />
        <Kpi
          icon={UserCheck}
          label="Today's Uniques"
          value={data.today_uniques}
          delta={data.today_uniques - data.yesterday_uniques}
          accent="#a780ff"
        />
        <Kpi
          icon={TrendingUp}
          label={`Last ${data.days}D Views`}
          value={data.range_total}
          sub={`${Intl.NumberFormat().format(data.range_uniques)} unique`}
          accent="var(--t-blue-glow)"
        />
        <Kpi
          icon={Inbox}
          label="Pending Contributions"
          value={data.counts.contributions_pending}
          accent="#f3c552"
          highlight={data.counts.contributions_pending > 0}
        />
      </div>

      {/* Chart panel */}
      <div className="panel corner-frame relative overflow-hidden p-4 md:p-6 mb-6" data-testid="admin-overview-chart">
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.18)_0%,_transparent_70%)] pointer-events-none" />

        <div className="relative flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-1">
              // Daily Page Views
            </div>
            <h3 className="font-display text-lg md:text-xl text-[var(--t-text)] uppercase">
              Traffic — Last {data.days} Days
            </h3>
          </div>
          <div className="inline-flex border border-[var(--t-border)] divide-x divide-[var(--t-border)]" data-testid="admin-overview-range">
            {RANGE_OPTIONS.map(o => (
              <button
                key={o.key}
                onClick={() => setDays(o.key)}
                className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition ${
                  days === o.key
                    ? "bg-[var(--t-blue)]/15 text-[var(--t-cyan)]"
                    : "text-[var(--t-text-dim)] hover:text-[var(--t-text)]"
                }`}
                data-testid={`admin-overview-range-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative h-64 md:h-80">
          <PageviewsChart daily={data.daily} />
        </div>
      </div>

      {/* Two-column: top paths + live feed */}
      <div className="grid lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        <TopPathsPanel paths={data.top_paths} total={data.range_total} />
        <LiveFeedPanel events={data.recent} />
      </div>

      {/* Secondary counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4" data-testid="admin-overview-counts">
        <MiniCount icon={FileText} label="Wiki Pages"     value={data.counts.wiki_pages} />
        <MiniCount icon={Package}  label="Items"          value={data.counts.items} />
        <MiniCount icon={Award}    label="Badges"         value={data.counts.badges} />
        <MiniCount icon={Mail}     label="Newsletter"     value={data.counts.newsletter_subs} />
        <MiniCount icon={Users}    label="Total Users"    value={data.counts.users} />
      </div>
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */

function Kpi({ icon: Icon, label, value, delta, accent, highlight, sub }) {
  return (
    <div
      className={`panel relative p-4 ${highlight ? "ring-1 ring-[var(--t-gold)]/40" : ""}`}
      style={{ borderColor: accent }}
    >
      <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: `linear-gradient(to bottom, ${accent}, transparent)` }} />
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--t-text-muted)]">
          {label}
        </span>
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      </div>
      <div className="font-display text-2xl md:text-3xl" style={{ color: accent }}>
        {Intl.NumberFormat().format(value || 0)}
      </div>
      {sub && (
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--t-text-muted)]">
          {sub}
        </div>
      )}
      {typeof delta === "number" && (
        <div className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em]">
          {delta > 0 && <><TrendingUp className="w-3 h-3 text-[#5fd99a]" /><span className="text-[#5fd99a]">+{delta} vs yesterday</span></>}
          {delta < 0 && <><TrendingDown className="w-3 h-3 text-[var(--t-red)]" /><span className="text-[var(--t-red)]">{delta} vs yesterday</span></>}
          {delta === 0 && <><Minus className="w-3 h-3 text-[var(--t-text-muted)]" /><span className="text-[var(--t-text-muted)]">flat</span></>}
        </div>
      )}
    </div>
  );
}

function MiniCount({ icon: Icon, label, value }) {
  return (
    <div className="border border-[var(--t-border)] bg-[var(--t-panel-2)]/60 px-3 py-3">
      <div className="flex items-center gap-2 text-[var(--t-text-muted)] mb-1">
        <Icon className="w-3 h-3" />
        <span className="font-mono text-[9px] uppercase tracking-[0.22em]">{label}</span>
      </div>
      <div className="font-display text-lg text-[var(--t-text)]">{Intl.NumberFormat().format(value || 0)}</div>
    </div>
  );
}

function PageviewsChart({ daily }) {
  // Format X axis: "Jun 21"
  const fmt = (d) => {
    const dt = new Date(d + "T00:00:00Z");
    return dt.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  };
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <defs>
          <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#66e6ff" stopOpacity={0.55} />
            <stop offset="100%" stopColor="#66e6ff" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="uvGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a780ff" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#a780ff" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 6" stroke="#1c2845" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={fmt}
          tick={{ fill: "#7c8aa6", fontSize: 11, fontFamily: "monospace" }}
          axisLine={{ stroke: "#1c2845" }}
          tickLine={false}
          minTickGap={20}
        />
        <YAxis
          tick={{ fill: "#7c8aa6", fontSize: 11, fontFamily: "monospace" }}
          axisLine={false}
          tickLine={false}
          width={48}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            background: "#0a1428",
            border: "1px solid #1c2845",
            borderRadius: 0,
            color: "#cfd8e6",
            fontFamily: "monospace",
            fontSize: 12,
          }}
          labelStyle={{ color: "#66e6ff", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
          labelFormatter={(d) => fmt(d)}
          formatter={(v, n) => [Intl.NumberFormat().format(v), n === "unique_count" ? "Unique Visitors" : "Views"]}
        />
        <Legend
          verticalAlign="top"
          align="right"
          height={26}
          iconType="square"
          iconSize={9}
          wrapperStyle={{
            fontFamily: "monospace",
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#7c8aa6",
          }}
          formatter={(value) => value === "unique_count" ? "Unique visitors" : "Page views"}
        />
        <Area
          type="monotone"
          dataKey="count"
          name="count"
          stroke="#66e6ff"
          strokeWidth={2}
          fill="url(#pvGrad)"
          dot={{ r: 2, fill: "#66e6ff" }}
          activeDot={{ r: 4, stroke: "#66e6ff", strokeWidth: 2, fill: "#0a1428" }}
        />
        <Area
          type="monotone"
          dataKey="unique_count"
          name="unique_count"
          stroke="#a780ff"
          strokeWidth={2}
          fill="url(#uvGrad)"
          dot={{ r: 2, fill: "#a780ff" }}
          activeDot={{ r: 4, stroke: "#a780ff", strokeWidth: 2, fill: "#0a1428" }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function TopPathsPanel({ paths, total }) {
  const max = useMemo(() => paths.reduce((m, p) => Math.max(m, p.count), 0) || 1, [paths]);
  return (
    <div className="panel corner-frame p-4 md:p-5" data-testid="admin-overview-top-paths">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-3">
        // Top Pages
      </div>
      {paths.length === 0 ? (
        <div className="text-xs text-[var(--t-text-muted)] italic">No views recorded yet.</div>
      ) : (
        <ul className="space-y-2">
          {paths.slice(0, 10).map((p) => (
            <li key={p.path} className="group">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="truncate text-[var(--t-text)] font-mono" title={p.path}>{p.path}</span>
                <span className="text-[var(--t-text-dim)] font-mono ml-3 shrink-0">
                  {Intl.NumberFormat().format(p.count)}
                  {p.unique_count > 0 && (
                    <span className="text-[#a780ff] ml-1.5">· {Intl.NumberFormat().format(p.unique_count)} uniq</span>
                  )}
                </span>
              </div>
              <div className="h-1 bg-[var(--t-panel-2)] border border-[var(--t-border)] overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${(p.count / max) * 100}%`,
                    background: "linear-gradient(90deg, var(--t-blue-deep), var(--t-cyan))",
                    boxShadow: "0 0 8px rgba(102,230,255,0.4)",
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LiveFeedPanel({ events }) {
  return (
    <div className="panel corner-frame p-4 md:p-5" data-testid="admin-overview-live-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)]">
          // Live Feed
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#5fd99a]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#5fd99a] pulse-glow" /> Live
        </span>
      </div>
      {events.length === 0 ? (
        <div className="text-xs text-[var(--t-text-muted)] italic">No recent views.</div>
      ) : (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {events.map((e, i) => (
            <li key={i} className="flex items-baseline gap-3 text-xs font-mono">
              <span className="text-[var(--t-text-muted)] shrink-0">
                {new Date(e.ts).toLocaleTimeString()}
              </span>
              <span className="truncate text-[var(--t-text)]" title={e.path}>{e.path}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
